import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertProps,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Fade,
  FormControl,
  Grid,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { FilePresent as FilePresentIcon } from '@mui/icons-material';

import {
  adminRespondToPenaltyAppeal,
  IPenaltyWaiver,
  IPenaltyWaiverFile,
  PENALTY_WAIVER_APPEAL_STATUSES,
} from '../../../../../../../../../store/V2/PenaltyWaiverSlice';
import { IUser } from '../../../../../../../../../store/UserSlice';
import { getShortName } from '../../../../../../../../../helper';
import {
  defaultTextFieldProps,
  TPTextField,
} from '../../../../../../../../../components/Inputs';

interface PenaltyAppealTabProps {
  penaltyWaiver: IPenaltyWaiver;
}

type TState = {
  adminNote: string;
  tutorNote: string;
  amount: string;
};

const amountViewCardContainerStyles: SxProps = {
  backgroundColor: '#F8FAFC',
  borderRadius: '8px',
};

const initialState: TState = {
  adminNote: '',
  tutorNote: '',
  amount: '',
};

const FileItemCard = ({ file }: { file: IPenaltyWaiverFile }) => {
  const cardContainerStyles: SxProps = {
    borderRadius: '8px',
    backgroundColor: '#F8FAFC',
  };

  return (
    <Card elevation={0} sx={cardContainerStyles}>
      <CardActionArea
        href={file.url}
        target="_blank"
        style={{ textDecoration: 'none' }}
      >
        <CardContent style={{ padding: '5px 16px' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap="10px">
              <FilePresentIcon fontSize="small" color="info" />
              <Typography fontSize="14px" fontWeight={500} color="info.main">
                {file.name}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const PenaltyAppealTab: FunctionComponent<PenaltyAppealTabProps> = ({
  penaltyWaiver,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [rejectLoading, setRejectLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const createdByUser = penaltyWaiver.createdBy as IUser;
  const respondedUser = penaltyWaiver.appealRespondedBy as IUser | undefined;
  const isRespondDisabled =
    state.adminNote.trim().length < 3 ||
    state.tutorNote.trim().length < 3 ||
    state.amount.trim() === '' ||
    Number.isNaN(parseFloat(state.amount)) ||
    parseFloat(state.amount.trim()) < 1;

  const createdByName = createdByUser
    ? getShortName(createdByUser.firstname, createdByUser.lastname, true)
    : '';

  const respondedByName = respondedUser
    ? getShortName(respondedUser.firstname, respondedUser.lastname, true)
    : '';

  const isResponded =
    penaltyWaiver.appealStatus !== PENALTY_WAIVER_APPEAL_STATUSES.PENDING;

  let appealStatusColor;

  const alertProps: AlertProps = {
    severity: 'success',
    color: 'success',
    sx: {
      backgroundColor: 'success.light',
      color: 'success.main',
    },
  };

  switch (penaltyWaiver.appealStatus) {
    case PENALTY_WAIVER_APPEAL_STATUSES.PENDING:
      appealStatusColor = 'warning';
      break;

    case PENALTY_WAIVER_APPEAL_STATUSES.ACCEPTED:
      appealStatusColor = 'success';
      break;

    case PENALTY_WAIVER_APPEAL_STATUSES.REJECTED:
      appealStatusColor = 'error';
      alertProps.severity = 'error';
      alertProps.color = 'error';
      alertProps.sx = {
        backgroundColor: 'error.light',
        color: 'error.main',
      };
      break;

    default:
      appealStatusColor = undefined;
      break;
  }

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      amount: penaltyWaiver.amount ? penaltyWaiver.amount.toString() : '',
      adminNote: penaltyWaiver.appealAdminNote
        ? penaltyWaiver.appealAdminNote
        : '',
      tutorNote: penaltyWaiver.appealTutorNote
        ? penaltyWaiver.appealTutorNote
        : '',
    }));
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const onReject = async () => {
    setRejectLoading(true);

    await dispatch(
      adminRespondToPenaltyAppeal({
        penaltyWaiverId: penaltyWaiver._id,
        adminNote: state.adminNote.trim(),
        tutorNote: state.tutorNote.trim(),
        amount: parseFloat(state.amount),
        response: PENALTY_WAIVER_APPEAL_STATUSES.REJECTED,
      })
    );

    setRejectLoading(false);
  };

  const onApprove = async () => {
    setApproveLoading(true);

    await dispatch(
      adminRespondToPenaltyAppeal({
        penaltyWaiverId: penaltyWaiver._id,
        adminNote: state.adminNote.trim(),
        tutorNote: state.tutorNote.trim(),
        amount: parseFloat(state.amount),
        response: PENALTY_WAIVER_APPEAL_STATUSES.ACCEPTED,
      })
    );

    setApproveLoading(false);
  };

  return (
    <Fade in>
      <div>
        <div style={{ marginBottom: '16px' }}>
          <Typography fontWeight={600} fontSize="16px">
            Penalty Appeal
          </Typography>
        </div>
        <div>
          <Grid container rowSpacing="16px">
            {isResponded ? (
              <Grid item xs={12}>
                <Alert {...alertProps}>
                  {penaltyWaiver.appealStatus ===
                  PENALTY_WAIVER_APPEAL_STATUSES.ACCEPTED
                    ? 'Penalty appeal is accepted'
                    : 'Penalty appeal is rejected'}
                </Alert>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap="8px"
              >
                {createdByName ? (
                  <Chip label={`Requested by: ${createdByName}`} />
                ) : null}
                {respondedByName ? (
                  <Chip label={`Responded by: ${respondedByName}`} />
                ) : null}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...defaultTextFieldProps}
                  sx={{ backgroundColor: '#F1F5F9' }}
                  multiline
                  rows={2}
                  label="Reason for appeal *"
                  placeholder="Enter here..."
                  defaultValue={penaltyWaiver.appealReason || ''}
                  InputProps={{ readOnly: true }}
                />
              </FormControl>
            </Grid>
            {penaltyWaiver.files && penaltyWaiver.files.length ? (
              <Grid item xs={12} padding="16px 0">
                <Stack gap="8px">
                  {penaltyWaiver.files.map((item) => (
                    <FileItemCard key={item.name} file={item} />
                  ))}
                </Stack>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...defaultTextFieldProps}
                  sx={{ backgroundColor: '#FFFFFF' }}
                  multiline
                  rows={2}
                  label="Admin note *"
                  placeholder="Enter here..."
                  value={state.adminNote}
                  InputProps={{ readOnly: isResponded }}
                  onChange={(event) =>
                    updateStateAttributes({ adminNote: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...defaultTextFieldProps}
                  sx={{ backgroundColor: '#FFFFFF' }}
                  multiline
                  rows={2}
                  label="Note for tutor *"
                  placeholder="Enter here..."
                  value={state.tutorNote}
                  InputProps={{ readOnly: isResponded }}
                  onChange={(event) =>
                    updateStateAttributes({ tutorNote: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={0} sx={amountViewCardContainerStyles}>
                <CardContent style={{ padding: '16px' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      fontSize="16px"
                      fontWeight={500}
                      color="#647488"
                    >
                      Penalty waive off amount ({penaltyWaiver.currency})
                    </Typography>
                    <div style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <TPTextField
                        size="small"
                        type="number"
                        placeholder="Enter here..."
                        value={state.amount}
                        InputProps={{ readOnly: isResponded }}
                        sx={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '8px',
                        }}
                        onChange={(event) =>
                          updateStateAttributes({
                            amount: event.target.value,
                          })
                        }
                      />
                    </div>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            {!isResponded ? (
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap="8px"
                >
                  <LoadingButton
                    disableElevation
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={approveLoading || isRespondDisabled}
                    loading={rejectLoading}
                    onClick={onReject}
                  >
                    Reject
                  </LoadingButton>
                  <LoadingButton
                    disableElevation
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={rejectLoading || isRespondDisabled}
                    loading={approveLoading}
                    onClick={onApprove}
                  >
                    Approve
                  </LoadingButton>
                </Stack>
              </Grid>
            ) : null}
          </Grid>
        </div>
      </div>
    </Fade>
  );
};

export default PenaltyAppealTab;
